import { z } from 'zod';
import {
  BUSINESS_TYPE_CODES,
  CARRIER_CODE_TYPES,
  CUSTOMER_TYPE_CODES,
  GRACE_STATUSES_AFTER,
  GRACE_STATUSES_DURING,
  NLAD_SERVICE_TYPES,
  PLAN_STATE_STATUSES,
  PLAN_TYPES,
  SALES_CHANNELS,
  TOPUP_PAYMENT_TYPES,
  TOPUP_TYPES,
  US_STATE_CODES,
} from '../enums';
import type { PlanStateStatus, PlanType, RecordStatus, SalesChannel } from '../enums';
import { listQuerySchema, objectId, optionalText, queryBool, recordStatus } from './common';

/**
 * Plan Config (reference PC232). Field list comes from the reference "Add Plan" form,
 * see docs/product-inventory.md appendix part 3. Money is integer cents.
 */

/* -------------------------------- Carriers -------------------------------- */

export interface CarrierDto {
  id: string;
  code: string;
  name: string;
  status: RecordStatus;
}

/** "Carrier Setup" dialog. Its fields were not observed in the reference: code, name and status are an assumption. */
export const carrierInputSchema = z.object({
  code: z.string().trim().min(1, 'Carrier code is required').max(20).toUpperCase(),
  name: z.string().trim().min(1, 'Carrier name is required').max(80),
  status: recordStatus.default('Active'),
});
export type CarrierInput = z.input<typeof carrierInputSchema>;
export const carrierUpdateSchema = carrierInputSchema.partial();

/* ------------------------------ Carrier codes ----------------------------- */

/** A wholesale plan at the carrier. `null` allowance means unlimited. */
export const carrierCodeInputSchema = z.object({
  carrierId: objectId,
  code: z.string().trim().min(1, 'Carrier code is required').max(30),
  type: z.enum(CARRIER_CODE_TYPES).default('Service Plan'),
  voiceMinutes: z.number().int().min(0).nullable().default(null),
  textCount: z.number().int().min(0).nullable().default(null),
  dataMb: z.number().int().min(0).nullable().default(null),
  billingCode: z.string().trim().max(40).optional().default(''),
  status: recordStatus.default('Active'),
});
export type CarrierCodeInput = z.input<typeof carrierCodeInputSchema>;
export const carrierCodeUpdateSchema = carrierCodeInputSchema.partial();

export const carrierCodeListQuerySchema = listQuerySchema.extend({
  code: optionalText(30),
  carrierId: objectId.optional(),
  type: z.enum(CARRIER_CODE_TYPES).optional(),
});

/**
 * A child row of Carrier Details: one plan on the carrier code, in one state (the reference's Plan
 * Name, Plan Type, Tribal, Plan State, Plan Status, Update Date, Plan Price; "Family Plan" is left
 * out: there are no family plans here).
 */
export interface CarrierCodePlanRow {
  planId: string;
  planName: string;
  planType: string;
  tribal: boolean | null;
  state: string;
  status: string;
  updatedAt: string;
  priceCents: number;
}

export interface CarrierCodeDto {
  id: string;
  carrier: { id: string; code: string; name: string };
  code: string;
  type: (typeof CARRIER_CODE_TYPES)[number];
  voiceMinutes: number | null;
  textCount: number | null;
  dataMb: number | null;
  billingCode: string;
  status: RecordStatus;
  /** Plans that use this code. */
  planCount: number;
  updatedAt: string;
}

/* ---------------------------------- Plans --------------------------------- */

export const planStateSchema = z.object({
  state: z.enum(US_STATE_CODES),
  status: z.enum(PLAN_STATE_STATUSES).default('DEMO'),
});

export const planTaxLineSchema = z.object({
  amountOrPercent: z.string().trim().max(20),
  transCode: z
    .string()
    .trim()
    .regex(/^\d{0,8}$/, 'Transaction code must be up to 8 digits'),
});

// Messages are the reference's own (validatePlanNewVersion, read on 2026-10-14).
const planFields = z.object({
  name: z
    .string({ required_error: 'Plan Name Should Not Blank.' })
    .trim()
    .min(1, 'Plan Name Should Not Blank.')
    .max(100),
  description: z.string().trim().max(500).optional().default(''),
  type: z.enum(PLAN_TYPES, { errorMap: () => ({ message: 'Plan Type Should Not Blank.' }) }),
  /** "Plan permission": the sales channels; at least one. */
  channels: z
    .array(z.enum(SALES_CHANNELS))
    .min(1, 'Please select at least one plan permission.')
    .default([]),
  taxable: z.boolean().default(true),
  customerType: z.enum(CUSTOMER_TYPE_CODES).default('01'),
  businessType: z.enum(BUSINESS_TYPE_CODES).default('14'),
  priceIncludesTax: z.boolean().default(false),
  taxLines: z.array(planTaxLineSchema).max(3).default([]),
  isUnlimited: z.boolean().default(false),
  unlimitedData: z.boolean().default(false),
  unlimitedText: z.boolean().default(false),
  unlimitedMinutes: z.boolean().default(false),
  carrierCodeId: objectId,
  /**
   * ZIP carrier routing: the plan's wholesale code on each other carrier ("Carrier Setup (ATT)"),
   * at most one per carrier. Left out, the codes stored stay as they are.
   */
  moreCarrierCodeIds: z.array(objectId).max(10).optional(),
  priceCents: z.number({ required_error: 'Price Should Not Blank.' }).int().min(0),
  activationFeeCents: z.number().int().min(0).default(0),
  /** "Maximum number of lines allowed". More than 1 makes this a family plan. */
  maxLines: z.number().int().min(1).max(10).default(1),
  /** Price per line, first line first. Length must equal maxLines when maxLines > 1. */
  linePricesCents: z.array(z.number().int().min(0)).max(10).default([]),
  tribal: z.boolean().nullable().default(null),
  display: z.boolean({ required_error: 'Display Status Should Not Blank.' }),
  notifyCustomer: z.boolean({ required_error: 'Customer notification setting is required' }),
  byod: z.boolean().nullable().default(null),
  nladServiceType: z.enum(NLAD_SERVICE_TYPES, {
    errorMap: () => ({ message: 'Please Select Plan Service Type.' }),
  }),
  gracePeriodDays: z
    .number({ required_error: 'Grace period days is required' })
    .int()
    .min(0)
    .max(365),
  statusDuringGrace: z.enum(GRACE_STATUSES_DURING).default('Suspend'),
  statusAfterGrace: z.enum(GRACE_STATUSES_AFTER).default('Suspend'),
  /** "Change to this plan in case of non-payment". */
  fallbackPlanId: objectId.nullable().default(null),
  states: z
    .array(planStateSchema, { required_error: 'Please select at least one state.' })
    .min(1, 'Please select at least one state.'),
});

const planRules = (v: z.infer<typeof planFields>, ctx: z.RefinementCtx) => {
  const seen = new Set<string>();
  v.states.forEach((s, i) => {
    if (seen.has(s.state))
      ctx.addIssue({ code: 'custom', path: ['states', i, 'state'], message: 'State listed twice' });
    seen.add(s.state);
  });
  // A taxable plan names the tax engine's code of its first tax line.
  if (v.taxable && !v.taxLines.some((l) => l.transCode))
    ctx.addIssue({
      code: 'custom',
      path: ['taxLines', 0, 'transCode'],
      message: 'Transcode should not be blank.',
    });
  if (v.isUnlimited && !(v.unlimitedData || v.unlimitedText || v.unlimitedMinutes))
    ctx.addIssue({
      code: 'custom',
      path: ['unlimitedData'],
      message: 'At least one checkbox should be checked.',
    });
  if (!v.isUnlimited && (v.unlimitedData || v.unlimitedText || v.unlimitedMinutes))
    ctx.addIssue({
      code: 'custom',
      path: ['isUnlimited'],
      message: 'Unlimited options need "Is Unlimited Plan" set to Yes',
    });
  if (v.maxLines > 1 && v.linePricesCents.length !== v.maxLines)
    ctx.addIssue({
      code: 'custom',
      path: ['linePricesCents'],
      message: 'Enter a price for every line',
    });
};

export const planInputSchema = planFields.superRefine(planRules);
export type PlanInput = z.input<typeof planInputSchema>;
export type PlanData = z.output<typeof planInputSchema>;

/** "Edit Plan Name" action on the reference list. */
export const planRenameSchema = z.object({ name: planFields.shape.name });

/** Body of the per-state status change on a plan's state row. */
export const planStateStatusSchema = z.object({ status: z.enum(PLAN_STATE_STATUSES) });

export const planListQuerySchema = listQuerySchema.extend({
  name: optionalText(),
  state: z.enum(US_STATE_CODES).optional(),
  carrierId: objectId.optional(),
  code: z.coerce.number().int().positive().optional(),
  status: z.enum(PLAN_STATE_STATUSES).optional(),
  display: queryBool,
  type: z.enum(PLAN_TYPES).optional(),
});
export type PlanListQuery = z.input<typeof planListQuerySchema>;

export interface PlanDto extends Omit<
  PlanData,
  'carrierCodeId' | 'moreCarrierCodeIds' | 'fallbackPlanId' | 'states'
> {
  id: string;
  /** Sequential plan code assigned on creation. */
  code: number;
  type: PlanType;
  channels: SalesChannel[];
  carrierCode: { id: string; code: string; carrier: { id: string; code: string; name: string } };
  /** Its codes on other carriers (ZIP carrier routing), in carrier code order. */
  moreCarrierCodes: Array<{
    id: string;
    code: string;
    carrier: { id: string; code: string; name: string };
  }>;
  fallbackPlan: { id: string; name: string } | null;
  states: Array<{ state: string; status: PlanStateStatus }>;
  createdAt: string;
  updatedAt: string;
}

/* --------------------------------- Top-ups -------------------------------- */

export const topupInputSchema = z.object({
  name: z.string().trim().min(1, 'Topup name is required').max(100),
  carrierId: objectId,
  paymentType: z.enum(TOPUP_PAYMENT_TYPES).default('Credit Card'),
  wholesaleCode: z.string().trim().max(30).optional().default(''),
  type: z.enum(TOPUP_TYPES).default('TOPUP'),
  costCents: z.number({ required_error: 'Topup cost is required' }).int().min(0),
  voiceMinutes: z.number().int().min(0).default(0),
  textCount: z.number().int().min(0).default(0),
  dataMb: z.number().int().min(0).default(0),
  status: recordStatus.default('Active'),
  states: z.array(z.enum(US_STATE_CODES)).min(1, 'Select at least one state'),
});
export type TopupInput = z.input<typeof topupInputSchema>;
export const topupUpdateSchema = topupInputSchema.partial();

export const topupListQuerySchema = listQuerySchema.extend({
  name: optionalText(),
  state: z.enum(US_STATE_CODES).optional(),
  status: recordStatus.optional(),
  carrierId: objectId.optional(),
});

export interface TopupDto extends z.output<typeof topupInputSchema> {
  id: string;
  carrier: { id: string; code: string; name: string };
  updatedAt: string;
}
